import React, { useState, useEffect } from 'react';
import { Sparkles, Check } from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';

/**
 * Screen 1: Enrichment Type Selection
 *
 * User selects from 10 predefined enrichment types using checkboxes.
 * Types are grouped by category for better organization.
 */
const EnrichmentTypeSelector = ({ onNext, onCancel, initialSelection = [] }) => {
  const [selectedTypes, setSelectedTypes] = useState(initialSelection);
  const [enrichmentTypes, setEnrichmentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Category colors for visual grouping
  const categoryColors = {
    contact: { bg: 'var(--color-info-bg)', border: 'var(--color-info)', icon: 'var(--color-info-hover)' },
    company_info: { bg: 'var(--color-success-bg)', border: 'var(--color-success)', icon: '#047857' },
    firmographic: { bg: 'var(--color-warning-bg-strong)', border: 'var(--color-warning)', icon: 'var(--color-warning-hover)' }
  };

  useEffect(() => {
    // Fetch enrichment types from backend
    fetchEnrichmentTypes();
  }, []);

  const fetchEnrichmentTypes = async () => {
    try {
      // For now, use hardcoded types (in production, fetch from backend)
      // TODO: Replace with API call to /api/enrichment/types
      const mockTypes = [
        {
          id: 'company_email',
          name: 'Company Email',
          description: 'Find the general contact email address for the company',
          icon: '📧',
          category: 'contact'
        },
        {
          id: 'company_phone',
          name: 'Company Phone',
          description: 'Find the main phone number for the company',
          icon: '📞',
          category: 'contact'
        },
        {
          id: 'company_executive',
          name: 'Executive Contact',
          description: 'Find CEO, founder, or key executive contact information',
          icon: '👔',
          category: 'contact'
        },
        {
          id: 'procurement_contact',
          name: 'Procurement Contact',
          description: 'Find procurement, purchasing, or supply chain manager',
          icon: '🛒',
          category: 'contact'
        },
        {
          id: 'company_address',
          name: 'Company Address',
          description: 'Find the headquarters or main office address',
          icon: '📍',
          category: 'company_info'
        },
        {
          id: 'company_social',
          name: 'Social Media Profiles',
          description: 'Find LinkedIn, Twitter, and other social media links',
          icon: '🔗',
          category: 'company_info'
        },
        {
          id: 'company_tech_stack',
          name: 'Technology Stack',
          description: 'Identify technologies and tools the company uses',
          icon: '💻',
          category: 'company_info'
        },
        {
          id: 'company_industry',
          name: 'Industry Classification',
          description: 'Identify the company\'s industry and sector',
          icon: '🏢',
          category: 'firmographic'
        },
        {
          id: 'company_size',
          name: 'Company Size',
          description: 'Find employee count and company size classification',
          icon: '👥',
          category: 'firmographic'
        },
        {
          id: 'company_revenue',
          name: 'Revenue',
          description: 'Find annual revenue and financial information',
          icon: '💰',
          category: 'firmographic'
        }
      ];

      setEnrichmentTypes(mockTypes);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching enrichment types:', err);
      setError('Failed to load enrichment types');
      setLoading(false);
    }
  };

  const toggleType = (typeId) => {
    if (selectedTypes.includes(typeId)) {
      setSelectedTypes(selectedTypes.filter(id => id !== typeId));
    } else {
      setSelectedTypes([...selectedTypes, typeId]);
    }
  };

  const handleNext = () => {
    if (selectedTypes.length === 0) {
      alert('Please select at least one enrichment type');
      return;
    }
    onNext(selectedTypes);
  };

  // Group types by category
  const typesByCategory = enrichmentTypes.reduce((acc, type) => {
    if (!acc[type.category]) {
      acc[type.category] = [];
    }
    acc[type.category].push(type);
    return acc;
  }, {});

  const categoryLabels = {
    contact: 'Contact Information',
    company_info: 'Company Information',
    firmographic: 'Firmographic Data'
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <div style={{ fontSize: 16, color: 'var(--color-text-muted)' }}>Loading enrichment types...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <div style={{ fontSize: 16, color: 'var(--color-danger-hover)', marginBottom: 16 }}>{error}</div>
        <Button onClick={fetchEnrichmentTypes}>Retry</Button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 12
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 48,
            height: 48,
            borderRadius: 12,
            background: 'var(--gradient-brand)',
            color: 'white'
          }}>
            <Sparkles size={24} />
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--color-text-primary)' }}>
              Select Enrichment Types
            </h2>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--color-text-muted)' }}>
              Choose what data you want to enrich (Step 1 of 2)
            </p>
          </div>
        </div>

        {/* Selection counter */}
        <div style={{
          display: 'inline-block',
          padding: '8px 16px',
          backgroundColor: selectedTypes.length > 0 ? 'var(--color-info-bg-strong)' : 'var(--color-bg-inset)',
          borderRadius: 8,
          fontSize: 14,
          fontWeight: 600,
          color: selectedTypes.length > 0 ? 'var(--color-info-fg)' : 'var(--color-text-muted)'
        }}>
          {selectedTypes.length} {selectedTypes.length === 1 ? 'type' : 'types'} selected
        </div>
      </div>

      {/* Enrichment type categories */}
      {Object.keys(typesByCategory).map((category) => {
        const colors = categoryColors[category] || categoryColors.contact;
        const types = typesByCategory[category];

        return (
          <div key={category} style={{ marginBottom: 24 }}>
            <h3 style={{
              fontSize: 16,
              fontWeight: 600,
              color: 'var(--color-text-primary)',
              marginBottom: 12,
              paddingLeft: 4
            }}>
              {categoryLabels[category] || category}
            </h3>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 12
            }}>
              {types.map((type) => {
                const isSelected = selectedTypes.includes(type.id);

                return (
                  <div
                    key={type.id}
                    onClick={() => toggleType(type.id)}
                    style={{
                      position: 'relative',
                      padding: 16,
                      backgroundColor: isSelected ? colors.bg : '#fff',
                      border: isSelected ? `2px solid ${colors.border}` : '2px solid var(--color-border)',
                      borderRadius: 8,
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      boxShadow: isSelected ? '0 4px 6px -1px rgba(0, 0, 0, 0.1)' : 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.borderColor = 'var(--color-border-strong)';
                        e.currentTarget.style.backgroundColor = 'var(--color-bg-subtle)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.borderColor = 'var(--color-border)';
                        e.currentTarget.style.backgroundColor = '#fff';
                      }
                    }}
                  >
                    {/* Checkbox indicator */}
                    <div style={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      width: 20,
                      height: 20,
                      borderRadius: 4,
                      backgroundColor: isSelected ? colors.border : '#fff',
                      border: isSelected ? 'none' : '2px solid var(--color-border-strong)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {isSelected && <Check size={14} color="#fff" strokeWidth={3} />}
                    </div>

                    {/* Icon */}
                    <div style={{
                      fontSize: 32,
                      marginBottom: 8
                    }}>
                      {type.icon}
                    </div>

                    {/* Name */}
                    <div style={{
                      fontSize: 15,
                      fontWeight: 600,
                      color: 'var(--color-text-primary)',
                      marginBottom: 4
                    }}>
                      {type.name}
                    </div>

                    {/* Description */}
                    <div style={{
                      fontSize: 13,
                      color: 'var(--color-text-muted)',
                      lineHeight: 1.4
                    }}>
                      {type.description}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Action buttons */}
      <div style={{
        display: 'flex',
        gap: 12,
        paddingTop: 24,
        borderTop: '1px solid var(--color-border)'
      }}>
        <Button
          onClick={handleNext}
          disabled={selectedTypes.length === 0}
          style={{
            flex: 1,
            opacity: selectedTypes.length === 0 ? 0.5 : 1
          }}
        >
          <Sparkles size={16} style={{ marginRight: 8 }} />
          Continue to CSV Upload
        </Button>
        <Button
          variant="secondary"
          onClick={onCancel}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default EnrichmentTypeSelector;
